import dayjs from "@workspace/domain/src/utils/dayjs";
import * as z from "zod";
import { AdminReservation } from "../reservation/schema";

// Date range filter schema
const DateRangeSchema = z.object({
  startDate: z
    .union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()])
    .optional()
    .default(dayjs().subtract(30, "day").startOf("day").toDate()),

  endDate: z
    .union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()])
    .optional()
    .default(dayjs().endOf("day").toDate()),
});

// Time series data point for charts
const TimeSeriesDataPointSchema = z.object({
  period: z.string(), // Format: YYYY-MM-DD (day), YYYY-MM-DD (week start), YYYY-MM (month), or YYYY (year)
  count: z.number(),
  revenue: z.number().optional(),
});

// Reservation status distribution item
const StatusDistributionItemSchema = z.object({
  status: AdminReservation.Reservation.shape.status,
  count: z.number(),
  percentage: z.number(),
});

// Trip distribution by category item
const TripDistributionItemSchema = z.object({
  category: z.string(),
  value: z.string(),
  count: z.number(),
  percentage: z.number(),
});

// Top trip schema
const TopTripSchema = z.object({
  id: z.string(),
  serial: z.string(),
  name: z.string(),
  currentRevenue: z.number(),
  currentReservations: z.number(),
  maxCapacity: z.number(),
  startDate: z.date(),
  targetRevenue: z.number().nullable(),
});

// Low capacity trip schema
const LowCapacityTripSchema = z.object({
  id: z.string(),
  serial: z.string(),
  name: z.string(),
  currentReservations: z.number(),
  maxCapacity: z.number(),
  remainingCapacity: z.number(),
  startDate: z.date(),
});

// Client segment schema
const ClientSegmentSchema = z.object({
  segment: z.enum(["light", "medium", "heavy"]), // light: 0-2, medium: 3-5, heavy: 6+
  count: z.number(),
  percentage: z.number(),
  totalSpent: z.number(),
});

// Top client schema
const TopClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string().nullable(),
  totalBookings: z.number(),
  totalSpent: z.number(),
  totalAmountDue: z.number(),
});

const ReservationsOverTimeSchema = z.array(TimeSeriesDataPointSchema);

const RevenueOverTimeSchema = z.array(TimeSeriesDataPointSchema);

const StatusDistributionSchema = z.array(StatusDistributionItemSchema);

const CancellationMetricsSchema = z.object({
  totalReservations: z.number(),
  cancelledReservations: z.number(),
  cancelledPercentage: z.number(),
  cancellationsWithRefund: z.number(),
  cancellationsWithoutRefund: z.number(),
});

const ReservationCompositionSchema = z.object({
  totalAdults: z.number(),
  totalChildren: z.number(),
  averageAdultsPerReservation: z.number(),
  averageChildrenPerReservation: z.number(),
});

const TopTripsByRevenueSchema = z.array(TopTripSchema);

const TopTripsByReservationsSchema = z.array(TopTripSchema);

const AudienceDistributionSchema = z.array(TripDistributionItemSchema);

const FoodDistributionSchema = z.array(TripDistributionItemSchema);

const TransportationDistributionSchema = z.array(TripDistributionItemSchema);

const AccessibilityDistributionSchema = z.array(TripDistributionItemSchema);

const GuidesDistributionSchema = z.array(TripDistributionItemSchema);

const TripsStartingSoonSchema = z.array(TopTripSchema);

const LowCapacityTripsSchema = z.array(LowCapacityTripSchema);

const ClientSegmentationSchema = z.array(ClientSegmentSchema);

const NewVsReturningClientsSchema = z.object({
  newClients: z.number(),
  totalClients: z.number(),
});

const ClientAcquisitionTimelineSchema = z.array(TimeSeriesDataPointSchema);

const TopClientsBySpendSchema = z.array(TopClientSchema);

const ClientsWithOutstandingDuesSchema = z.array(TopClientSchema);

// Response Schemas for individual endpoints

// Overview
export const OverviewTotalReservationsSchema = z.number();
export const OverviewNewClientsSchema = z.number();
export const OverviewTotalAmountDueSchema = z.number();
export const OverviewTotalClientsSchema = z.number();

// Reservations
export const ReservationsOverTimeResponseSchema = ReservationsOverTimeSchema;
export const RevenueOverTimeResponseSchema = RevenueOverTimeSchema;
export const ReservationsStatusDistributionResponseSchema = StatusDistributionSchema;
export const ReservationsCancellationMetricsResponseSchema = CancellationMetricsSchema;
export const ReservationsAverageRevenueResponseSchema = z.number();
export const ReservationsCompositionResponseSchema = ReservationCompositionSchema;
export const ReservationsAverageBookingLeadTimeResponseSchema = z.number();

// Trips
export const TripsTopByRevenueResponseSchema = TopTripsByRevenueSchema;
export const TripsTopByReservationsResponseSchema = TopTripsByReservationsSchema;
export const TripsStartingSoonResponseSchema = TripsStartingSoonSchema;
export const TripsLowCapacityResponseSchema = LowCapacityTripsSchema;
export const TripsAudienceDistributionResponseSchema = AudienceDistributionSchema;
export const TripsFoodDistributionResponseSchema = FoodDistributionSchema;
export const TripsTransportationDistributionResponseSchema = TransportationDistributionSchema;
export const TripsAccessibilityDistributionResponseSchema = AccessibilityDistributionSchema;
export const TripsGuidesDistributionResponseSchema = GuidesDistributionSchema;

// Clients
export const ClientsSegmentationResponseSchema = ClientSegmentationSchema;
export const ClientsNewVsReturningResponseSchema = NewVsReturningClientsSchema;
export const ClientsAcquisitionsOverTimeResponseSchema = ClientAcquisitionTimelineSchema;
export const ClientsTopBySpendResponseSchema = TopClientsBySpendSchema;
export const ClientsOutstandingDuesResponseSchema = ClientsWithOutstandingDuesSchema;

// Payments
export const PaymentsCompletionRateResponseSchema = z.number();
export const PaymentsTotalPaidResponseSchema = z.number();
export const PaymentsTotalDueResponseSchema = z.number();
export const PaymentsTotalRefundedResponseSchema = z.number();

export const AdminAnalytics = {
  DateRange: DateRangeSchema,
  // Overview
  OverviewTotalReservations: OverviewTotalReservationsSchema,
  OverviewNewClients: OverviewNewClientsSchema,
  OverviewTotalAmountDue: OverviewTotalAmountDueSchema,
  OverviewTotalClients: OverviewTotalClientsSchema,
  // Reservations
  ReservationsOverTime: ReservationsOverTimeResponseSchema,
  RevenueOverTime: RevenueOverTimeResponseSchema,
  ReservationsStatusDistribution: ReservationsStatusDistributionResponseSchema,
  ReservationsCancellationMetrics: ReservationsCancellationMetricsResponseSchema,
  ReservationsAverageRevenue: ReservationsAverageRevenueResponseSchema,
  ReservationsComposition: ReservationsCompositionResponseSchema,
  ReservationsAverageBookingLeadTime: ReservationsAverageBookingLeadTimeResponseSchema,
  // Trips
  TripsTopByRevenue: TripsTopByRevenueResponseSchema,
  TripsTopByReservations: TripsTopByReservationsResponseSchema,
  TripsStartingSoon: TripsStartingSoonResponseSchema,
  TripsLowCapacity: TripsLowCapacityResponseSchema,
  TripsAudienceDistribution: TripsAudienceDistributionResponseSchema,
  TripsFoodDistribution: TripsFoodDistributionResponseSchema,
  TripsTransportationDistribution: TripsTransportationDistributionResponseSchema,
  TripsAccessibilityDistribution: TripsAccessibilityDistributionResponseSchema,
  TripsGuidesDistribution: TripsGuidesDistributionResponseSchema,
  // Clients
  ClientsSegmentation: ClientsSegmentationResponseSchema,
  ClientsNewVsReturning: ClientsNewVsReturningResponseSchema,
  ClientsAcquisitionsOverTime: ClientsAcquisitionsOverTimeResponseSchema,
  ClientsTopBySpend: ClientsTopBySpendResponseSchema,
  ClientsOutstandingDues: ClientsOutstandingDuesResponseSchema,
  // Payments
  PaymentsCompletionRate: PaymentsCompletionRateResponseSchema,
  PaymentsTotalPaid: PaymentsTotalPaidResponseSchema,
  PaymentsTotalDue: PaymentsTotalDueResponseSchema,
  PaymentsTotalRefunded: PaymentsTotalRefundedResponseSchema,
};
